import { AlertCircle, CreditCard } from "lucide-react";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { useIsFreePlanAccount } from "@/hooks/usePlanStatus";
import { useOrganization } from "@/contexts/OrganizationContext";

export default function FreePlanBanner() {
  const isFreePlan = useIsFreePlanAccount();
  const { currentOrganization } = useOrganization();

  // Don't show if not on free plan
  if (!isFreePlan) {
    return null;
  }

  // Check if account is locked (archived) or just restricted (canceled)
  const isLocked = currentOrganization?.lifecycle === 'archived';
  const priority = isLocked ? 'high' : 'medium';

  const getBannerStyle = () => {
    if (isLocked) {
      return 'bg-red-50 border-red-200 text-red-900';
    }
    return 'bg-orange-50 border-orange-200 text-orange-900';
  };

  const getIconColor = () => {
    if (isLocked) {
      return 'text-red-600';
    }
    return 'text-orange-600';
  };

  const getMessage = () => {
    if (isLocked) {
      return {
        title: 'Account Inactive - Choose a Plan',
        message: 'Your account is currently inactive. Select a plan to reactivate your account and access all features.',
        ctaText: 'Choose Plan'
      };
    }
    return {
      title: 'Free Plan - Limited Access',
      message: 'You\'re on a free plan with restricted features. Upgrade to unlock messaging, calling, and advanced features.',
      ctaText: 'Upgrade Now'
    };
  };

  const { title, message, ctaText } = getMessage();

  return (
    <div className={`border-b ${getBannerStyle()}`} data-testid="banner-free-plan">
      <div className="container mx-auto px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3 flex-1">
            <div className="flex-shrink-0">
              {priority === 'high' ? (
                <AlertCircle className={`w-5 h-5 ${getIconColor()}`} />
              ) : (
                <CreditCard className={`w-5 h-5 ${getIconColor()}`} />
              )}
            </div>
            
            <div className="flex-1">
              <p className="font-semibold text-sm md:text-base" data-testid="text-free-plan-title">
                {title}
              </p>
              <p className="text-xs md:text-sm opacity-90 mt-0.5" data-testid="text-free-plan-message">
                {message}
              </p>
            </div>

            <div className="flex items-center space-x-2">
              <Link href="/plans">
                <Button
                  size="sm"
                  className={`${priority === 'high' ? 'bg-red-600 hover:bg-red-700' : 'bg-orange-600 hover:bg-orange-700'}`}
                  data-testid="button-choose-plan"
                >
                  {ctaText}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
